import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { isGreater, type I18nKey } from '@brao/shared';
import { api, ApiError, type ImportSummary, type PackageHistoryRow } from '../api';
import {
  agentsApi,
  type AgentBudget,
  type AgentInstance,
  type AgentModel,
  type AgentPolicy,
  type AgentTemplate,
  type AgentTool,
  type CredentialOption,
  type LibraryStatus,
  type SideEffect,
  type SupervisionLevel,
} from '../api/agents';
import type { TaskBusiness } from '../api/tasks';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';
import './agents.css';
import { SkillAgents } from './SkillAgents';

const fmt = (iso: string) => new Date(iso).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Enum → key maps instead of interpolated keys: the i18n guard only sees plain string literals in a
 * `t(...)` call, so a key built with `${}` would never be checked (see scripts/guards/i18n-keys.mjs).
 */
const SUPERVISION_KEY: Record<SupervisionLevel, I18nKey> = {
  every_step: 'agents.sup.everyStep',
  plan_and_irreversible: 'agents.sup.planAndIrreversible',
  plan_only: 'agents.sup.planOnly',
};
const STATUS_KEY: Record<LibraryStatus, I18nKey> = {
  draft: 'agents.status.draft',
  active: 'agents.status.active',
  deprecated: 'agents.status.deprecated',
  archived: 'agents.status.archived',
};
const EFFECT_KEY: Record<SideEffect, I18nKey> = {
  none: 'agents.effect.none',
  reversible: 'agents.effect.reversible',
  irreversible: 'agents.effect.irreversible',
};
/** Machine-readable API errors the Agents endpoints answer with, in Ukrainian. */
const ERROR_KEY: Record<string, I18nKey> = {
  version_conflict: 'agents.error.versionConflict',
  template_not_active: 'agents.error.templateNotActive',
  template_already_active: 'agents.error.templateAlreadyActive',
  model_not_in_catalog: 'agents.error.modelNotInCatalog',
  tool_not_in_office_policy: 'agents.error.toolNotInOfficePolicy',
  tool_not_enabled: 'agents.error.toolNotEnabled',
  model_exists: 'agents.error.modelExists',
  agent_role_missing: 'agents.error.agentRoleMissing',
  credential_not_found: 'agents.error.credentialNotFound',
};

const SUPERVISIONS: SupervisionLevel[] = ['every_step', 'plan_and_irreversible', 'plan_only'];
const INSTANCE_STATUSES: LibraryStatus[] = ['draft', 'active', 'deprecated', 'archived'];

function errorText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : e instanceof Error ? e.message : '';
  const key = ERROR_KEY[code];
  return key ? t(key) : code || t('common.error');
}

const statusBadge = (s: LibraryStatus) => <span className={s === 'active' ? 'badge ok' : 'badge'}>{t(STATUS_KEY[s])}</span>;

type Section = 'skill' | 'instances' | 'templates' | 'tools' | 'models' | 'library';

export function Agents({ canRead, canManage, canManageTools, canManageModels, canManageLibrary }: { canRead: boolean; canManage: boolean; canManageTools: boolean; canManageModels: boolean; canManageLibrary: boolean }) {
  const [section, setSection] = useState<Section>(canRead ? 'skill' : 'library');
  const sections = useMemo<Section[]>(() => [...(canRead ? (['skill', 'instances', 'templates', 'tools', 'models'] as Section[]) : []), ...(canManageLibrary ? (['library'] as Section[]) : [])], [canRead, canManageLibrary]);
  const label: Record<Section, I18nKey> = { skill: 'agents.section.skill', instances: 'agents.section.instances', templates: 'agents.section.templates', tools: 'agents.section.tools', models: 'agents.section.models', library: 'agents.library' };

  return (
    <>
      <h1>{t('agents.title')}</h1>
      {section !== 'skill' && <p className="muted">{t('agents.intro')}</p>}
      {sections.length > 1 && (
        <div className="row agent-sections" role="tablist" aria-label={t('agents.title')}>
          {sections.map((s) => (
            <button key={s} type="button" role="tab" className={`chip${section === s ? ' on' : ''}`} aria-selected={section === s} onClick={() => setSection(s)}>
              {t(label[s])}
            </button>
          ))}
        </div>
      )}
      {section === 'skill' && canRead && <SkillAgents />}
      {section === 'instances' && canRead && <Instances canManage={canManage} />}
      {section === 'templates' && canRead && <Templates canManage={canManage} />}
      {section === 'tools' && canRead && <Tools canManage={canManageTools} canManagePolicy={canManage} />}
      {section === 'models' && canRead && <Models canManage={canManageModels} />}
      {section === 'library' && canManageLibrary && <Library />}
    </>
  );
}

// ---------------------------------------------------------------- instances

function Instances({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<AgentInstance[] | null>(null);
  const [businesses, setBusinesses] = useState<TaskBusiness[]>([]);
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [models, setModels] = useState<AgentModel[]>([]);
  const [businessId, setBusinessId] = useState('');
  const [form, setForm] = useState<AgentInstance | 'new' | null>(null);
  const [policyFor, setPolicyFor] = useState<AgentInstance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const load = () =>
    agentsApi
      .instances(businessId || undefined)
      .then(setRows)
      .catch((e) => setError(errorText(e)));

  useEffect(() => {
    agentsApi.businesses().then(setBusinesses).catch((e) => setError(errorText(e)));
    agentsApi.templates().then(setTemplates).catch((e) => setError(errorText(e)));
    agentsApi.models().then(setModels).catch((e) => setError(errorText(e)));
  }, []);

  useEffect(() => {
    setRows(null);
    void load();
  }, [businessId]);

  const activeBusinesses = useMemo(() => businesses.filter((b) => b.status === 'active'), [businesses]);
  const enabledModels = useMemo(() => models.filter((m) => m.enabled), [models]);
  const usableTemplates = useMemo(() => templates.filter((tpl) => tpl.status !== 'archived'), [templates]);

  const columns: Column<AgentInstance>[] = [
    {
      key: 'name',
      title: t('agents.col.name'),
      // The name opens the instance drawer, edit or read-only depending on the permission
      // (registry pattern "row-select", as on Processes).
      render: (r) => (
        <button type="button" className="row-select" style={{ all: 'unset', cursor: 'pointer', color: 'inherit' }} onClick={() => setForm(r)} title={canManage ? t('agents.action.edit') : t('agents.action.view')}>
          {r.name}
        </button>
      ),
      sortValue: (r) => r.name,
      wrap: true,
    },
    { key: 'business', title: t('agents.col.business'), render: (r) => r.business?.name ?? <span className="muted">—</span>, sortValue: (r) => r.business?.name ?? null },
    { key: 'template', title: t('agents.col.template'), render: (r) => `${r.template.name} ${t('agents.templates.version', { n: r.templateVersion })}`, sortValue: (r) => r.template.name },
    { key: 'model', title: t('agents.col.model'), render: (r) => r.model, sortValue: (r) => r.model },
    { key: 'supervision', title: t('agents.col.supervision'), render: (r) => t(SUPERVISION_KEY[r.supervisionLevel]), sortValue: (r) => r.supervisionLevel },
    { key: 'spent', title: t('agents.col.spent'), render: (r) => <SpentCell instance={r} />, sortValue: (r) => Number(r.spentThisMonth) },
    { key: 'status', title: t('agents.col.status'), render: (r) => statusBadge(r.status), sortValue: (r) => r.status },
    {
      key: 'actions',
      title: '',
      // Only "Політика" is a button: eight columns plus two buttons do not fit at 1280px, and the
      // edit/view drawer is reached by clicking the name (registry pattern "row-select").
      render: (r) => (
        <button type="button" className="btn secondary small" onClick={() => setPolicyFor(r)}>{t('agents.action.policy')}</button>
      ),
    },
  ];

  return (
    <>
      {conflict && (
        <div className="conflict">
          <span>{t('agents.error.versionConflict')}</span>
          <button type="button" className="btn secondary small" onClick={() => { setConflict(false); setForm(null); void load(); }}>{t('agents.reload')}</button>
        </div>
      )}
      <div className="row between">
        <h2 style={{ margin: 0 }}>{t('agents.instances.title')}</h2>
        {canManage && (
          <button type="button" className="btn" onClick={() => setForm('new')} disabled={activeBusinesses.length === 0 || usableTemplates.length === 0}>
            {t('agents.instances.new')}
          </button>
        )}
      </div>
      <div className="row filters" style={{ margin: 'var(--sp-3) 0' }}>
        <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} aria-label={t('agents.col.business')}>
          <option value="">{t('agents.filter.allBusinesses')}</option>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>
      {activeBusinesses.length === 0 && <p className="muted">{t('agents.instances.noBusinesses')}</p>}
      {activeBusinesses.length > 0 && usableTemplates.length === 0 && <p className="muted">{t('agents.instances.noTemplates')}</p>}
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}
      <div className="agent-table">
        <Table columns={columns} rows={rows ?? []} rowKey={(r) => r.id} empty={rows === null ? t('common.loading') : t('agents.instances.empty')} defaultSort={{ key: 'name', dir: 'asc' }} />
      </div>

      {form && (
        <InstanceDrawer
          key={form === 'new' ? 'new' : `${form.id}:${form.version}`}
          instance={form === 'new' ? null : form}
          businesses={activeBusinesses}
          templates={usableTemplates}
          models={enabledModels}
          canManage={canManage}
          onClose={() => setForm(null)}
          onSaved={() => { setForm(null); void load(); }}
          onConflict={() => setConflict(true)}
        />
      )}
      {policyFor && <PolicyDrawer instance={policyFor} canManage={canManage} onClose={() => setPolicyFor(null)} onSaved={() => void load()} />}
    </>
  );
}

/** Spent / budget for the current month; above the soft limit the whole cell becomes a warn badge. */
function SpentCell({ instance }: { instance: AgentInstance }) {
  if (!instance.budget) return <span className="muted">{t('agents.noBudget')}</span>;
  const text = t('agents.spent', { spent: money(instance.spentThisMonth), limit: money(instance.budget.hard), currency: instance.budget.currency });
  return isGreater(instance.spentThisMonth, instance.budget.soft) ? <span className="badge warn">{text}</span> : <span>{text}</span>;
}

/** Two decimals is what the panel shows; the six-decimal string stays the source of truth. */
const money = (v: string) => Number(v).toFixed(2);

function InstanceDrawer({ instance, businesses, templates, models, canManage, onClose, onSaved, onConflict }: { instance: AgentInstance | null; businesses: TaskBusiness[]; templates: AgentTemplate[]; models: AgentModel[]; canManage: boolean; onClose: () => void; onSaved: () => void; onConflict: () => void }) {
  const [templateId, setTemplateId] = useState(instance?.templateId ?? templates[0]?.id ?? '');
  const [businessId, setBusinessId] = useState(instance?.businessId ?? businesses[0]?.id ?? '');
  const [name, setName] = useState(instance?.name ?? '');
  const [model, setModel] = useState(instance?.model ?? models[0]?.model ?? '');
  const [credentialId, setCredentialId] = useState(instance?.overrides.credentialId ?? '');
  const [supervision, setSupervision] = useState<SupervisionLevel>(instance?.supervisionLevel ?? 'every_step');
  const [status, setStatus] = useState<LibraryStatus>(instance?.status ?? 'draft');
  const [instructionAdditions, setInstructionAdditions] = useState(instance?.overrides.instructionAdditions ?? '');
  const [tone, setTone] = useState(instance?.overrides.tone ?? '');
  const [language, setLanguage] = useState(instance?.overrides.language ?? '');
  const [softLimit, setSoftLimit] = useState(instance?.budget?.soft ?? '10.0000');
  const [hardLimit, setHardLimit] = useState(instance?.budget?.hard ?? '20.0000');
  const [credentials, setCredentials] = useState<CredentialOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (canManage) agentsApi.credentials().then(setCredentials).catch(() => setCredentials([]));
  }, [canManage]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    const overrides = {
      ...(instructionAdditions.trim() ? { instructionAdditions: instructionAdditions.trim() } : {}),
      ...(tone.trim() ? { tone: tone.trim() } : {}),
      ...(language.trim() ? { language: language.trim() } : {}),
      ...(credentialId ? { credentialId } : {}),
    };
    try {
      if (instance) {
        await agentsApi.updateInstance(instance.id, { version: instance.version, name: name.trim(), model, overrides, supervisionLevel: supervision, status });
        if (instance.budget && (softLimit !== instance.budget.soft || hardLimit !== instance.budget.hard)) await agentsApi.putBudget(instance.id, { softLimit, hardLimit });
      } else {
        await agentsApi.createInstance({ templateId, businessId, name: name.trim(), model, overrides, supervisionLevel: supervision, budget: { softLimit, hardLimit } });
      }
      setSaved(true);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'version_conflict') onConflict();
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const ro = !canManage;
  const title = instance ? t('agents.form.edit', { name: instance.name }) : t('agents.form.new');

  return (
    <Drawer title={title} onClose={onClose}>
      <form className="form" onSubmit={submit} aria-label={title}>
        <label>
          {t('agents.form.template')}
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={!!instance || ro} required>
            {(instance ? [{ id: instance.templateId, name: instance.template.name }] : templates).map((tpl) => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
            ))}
          </select>
        </label>
        <label>
          {t('agents.form.business')}
          <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} disabled={!!instance || ro} required>
            {(instance && instance.business ? [instance.business] : businesses).map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </label>
        <label>
          {t('agents.form.name')}
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} readOnly={ro} />
        </label>
        <div className="row">
          <label>
            {t('agents.form.model')}
            <select value={model} onChange={(e) => setModel(e.target.value)} disabled={ro} required>
              {models.some((m) => m.model === model) ? null : <option value={model}>{model}</option>}
              {models.map((m) => (
                <option key={m.id} value={m.model}>{m.model}</option>
              ))}
            </select>
          </label>
          <label>
            {t('agents.form.supervision')}
            <select value={supervision} onChange={(e) => setSupervision(e.target.value as SupervisionLevel)} disabled={ro}>
              {SUPERVISIONS.map((s) => (
                <option key={s} value={s}>{t(SUPERVISION_KEY[s])}</option>
              ))}
            </select>
          </label>
        </div>
        <label>
          {t('agents.form.credential')}
          <select value={credentialId} onChange={(e) => setCredentialId(e.target.value)} disabled={ro}>
            <option value="">{t('agents.form.none')}</option>
            {credentials.map((c) => (
              <option key={c.id} value={c.id}>{`${c.name} · ${c.provider}`}</option>
            ))}
          </select>
        </label>
        {instance && (
          <label>
            {t('agents.form.status')}
            <select value={status} onChange={(e) => setStatus(e.target.value as LibraryStatus)} disabled={ro}>
              {INSTANCE_STATUSES.map((s) => (
                <option key={s} value={s}>{t(STATUS_KEY[s])}</option>
              ))}
            </select>
          </label>
        )}
        <strong className="muted">{t('agents.form.overrides')}</strong>
        <label>
          {t('agents.form.instructionAdditions')}
          <textarea rows={3} value={instructionAdditions} onChange={(e) => setInstructionAdditions(e.target.value)} readOnly={ro} />
        </label>
        <div className="row">
          <label>
            {t('agents.form.tone')}
            <input value={tone} onChange={(e) => setTone(e.target.value)} maxLength={500} readOnly={ro} />
          </label>
          <label>
            {t('agents.form.language')}
            <input value={language} onChange={(e) => setLanguage(e.target.value)} maxLength={10} readOnly={ro} />
          </label>
        </div>
        <strong className="muted">{t('agents.form.budget')}</strong>
        <div className="row">
          <label>
            {t('agents.form.softLimit')}
            <input value={softLimit} onChange={(e) => setSoftLimit(e.target.value)} inputMode="decimal" readOnly={ro} />
          </label>
          <label>
            {t('agents.form.hardLimit')}
            <input value={hardLimit} onChange={(e) => setHardLimit(e.target.value)} inputMode="decimal" readOnly={ro} />
          </label>
        </div>
        <div className="note">{t('agents.form.hint')}</div>
        {error && <div className="error">{error}</div>}
        {saved && !error && <div className="ok">{t('agents.form.saved')}</div>}
        <div className="row">
          {canManage && <button className="btn" type="submit" disabled={busy || !name.trim() || !model}>{instance ? t('common.save') : t('agents.form.create')}</button>}
          <button className="btn secondary" type="button" onClick={onClose}>{t('common.close')}</button>
        </div>
      </form>
    </Drawer>
  );
}

// ------------------------------------------------------------------ policy

function PolicyDrawer({ instance, canManage, onClose, onSaved }: { instance: AgentInstance | null; canManage: boolean; onClose: () => void; onSaved: () => void }) {
  const [policy, setPolicy] = useState<AgentPolicy | null>(null);
  const [tools, setTools] = useState<AgentTool[]>([]);
  const [allowed, setAllowed] = useState<string[]>([]);
  const [limits, setLimits] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const read = instance ? agentsApi.instancePolicy(instance.id) : agentsApi.officePolicy();
    Promise.all([read, agentsApi.tools()])
      .then(([p, all]) => {
        setPolicy(p);
        setTools(all.filter((x) => x.enabled));
        setAllowed(p.allowedToolIds);
        setLimits({
          maxCostPerRun: p.maxCostPerRun ?? '',
          maxCostPerDay: p.maxCostPerDay ?? '',
          maxToolCalls: p.maxToolCalls === null ? '' : String(p.maxToolCalls),
          maxExecutionTimeSec: p.maxExecutionTimeSec === null ? '' : String(p.maxExecutionTimeSec),
          maxRetries: String(p.maxRetries),
          approvalThreshold: p.approvalThreshold ?? '',
        });
      })
      .catch((e) => setError(errorText(e)));
  }, [instance]);

  const office = policy?.officeAllowedToolIds ?? null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    const num = (k: string) => (limits[k]?.trim() ? Number(limits[k]) : null);
    const dec = (k: string) => (limits[k]?.trim() ? limits[k]!.trim() : null);
    const body = {
      allowedToolIds: allowed,
      maxCostPerRun: dec('maxCostPerRun'),
      maxCostPerDay: dec('maxCostPerDay'),
      maxToolCalls: num('maxToolCalls'),
      maxExecutionTimeSec: num('maxExecutionTimeSec'),
      maxRetries: Number(limits.maxRetries || '2'),
      approvalThreshold: dec('approvalThreshold'),
    };
    try {
      setPolicy(instance ? await agentsApi.putInstancePolicy(instance.id, body) : await agentsApi.putOfficePolicy(body));
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string) => setAllowed((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const title = instance ? t('agents.policy.instance', { name: instance.name }) : t('agents.policy.office');
  const fields: Array<{ key: string; label: I18nKey; decimal: boolean }> = [
    { key: 'maxCostPerRun', label: 'agents.policy.maxCostPerRun', decimal: true },
    { key: 'maxCostPerDay', label: 'agents.policy.maxCostPerDay', decimal: true },
    { key: 'maxToolCalls', label: 'agents.policy.maxToolCalls', decimal: false },
    { key: 'maxExecutionTimeSec', label: 'agents.policy.maxExecutionTimeSec', decimal: false },
    { key: 'maxRetries', label: 'agents.policy.maxRetries', decimal: false },
    { key: 'approvalThreshold', label: 'agents.policy.approvalThreshold', decimal: true },
  ];

  return (
    <Drawer title={title} onClose={onClose} badge={policy && policy.version > 0 ? t('agents.policy.version', { n: policy.version }) : undefined}>
      <form className="form agent-drawer" onSubmit={submit} aria-label={title}>
        <strong className="muted">{t('agents.policy.allowedTools')}</strong>
        <div className="tool-list">
          {tools.length === 0 && <span className="muted">{t('agents.policy.noTools')}</span>}
          {tools.map((tool) => {
            const outsideOffice = office !== null && !office.includes(tool.id);
            return (
              <label key={tool.id}>
                <input type="checkbox" checked={allowed.includes(tool.id)} disabled={!canManage || outsideOffice} onChange={() => toggle(tool.id)} />
                <span>{tool.ref}</span>
                <span className={tool.sideEffect === 'irreversible' ? 'badge danger' : tool.sideEffect === 'reversible' ? 'badge ok' : 'badge'}>{t(EFFECT_KEY[tool.sideEffect])}</span>
                {outsideOffice && <span className="badge warn">{t('agents.policy.notInOffice')}</span>}
              </label>
            );
          })}
        </div>
        {allowed.length === 0 && <div className="note">{t('agents.policy.empty')}</div>}
        {fields.map((f) => (
          <label key={f.key}>
            {t(f.label)}
            <input value={limits[f.key] ?? ''} onChange={(e) => setLimits((prev) => ({ ...prev, [f.key]: e.target.value }))} inputMode={f.decimal ? 'decimal' : 'numeric'} readOnly={!canManage} />
          </label>
        ))}
        {error && <div className="error">{error}</div>}
        {saved && !error && <div className="ok">{t('agents.policy.saved')}</div>}
        <div className="row">
          {canManage && <button className="btn" type="submit" disabled={busy}>{t('common.save')}</button>}
          <button className="btn secondary" type="button" onClick={onClose}>{t('common.close')}</button>
        </div>
      </form>
    </Drawer>
  );
}

// ---------------------------------------------------------------- templates

function Templates({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<AgentTemplate[] | null>(null);
  const [open, setOpen] = useState<AgentTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () => agentsApi.templates().then(setRows).catch((e) => setError(errorText(e)));
  useEffect(() => { void load(); }, []);

  const activate = async (row: AgentTemplate) => {
    setError(null);
    setNotice(null);
    try {
      await agentsApi.activateTemplate(row.id);
      setNotice(t('agents.templates.activated'));
      setOpen(null);
      await load();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const columns: Column<AgentTemplate>[] = [
    { key: 'slug', title: t('agents.templates.col.slug'), render: (r) => r.slug, sortValue: (r) => r.slug },
    {
      key: 'name',
      title: t('agents.col.name'),
      // The name opens the read-only instruction drawer (registry pattern "row-select", as on Processes).
      render: (r) => (
        <button type="button" className="row-select" style={{ all: 'unset', cursor: 'pointer', color: 'inherit' }} onClick={() => setOpen(r)}>
          {r.name}
        </button>
      ),
      sortValue: (r) => r.name,
      wrap: true,
    },
    { key: 'version', title: t('agents.templates.col.version'), render: (r) => t('agents.templates.version', { n: r.currentVersion }), sortValue: (r) => r.currentVersion },
    { key: 'status', title: t('agents.col.status'), render: (r) => statusBadge(r.status), sortValue: (r) => r.status },
    { key: 'origin', title: t('agents.templates.col.origin'), render: (r) => t(r.shipped ? 'agents.templates.shipped' : 'agents.templates.own'), sortValue: (r) => (r.shipped ? 1 : 0) },
    { key: 'instances', title: t('agents.templates.col.instances'), render: (r) => r.instanceCount, sortValue: (r) => r.instanceCount },
    {
      key: 'actions',
      title: '',
      render: (r) => (
        <span className="row">
          <button type="button" className="btn secondary small" onClick={() => setOpen(r)}>{t('agents.action.view')}</button>
          {canManage && r.status === 'draft' && <button type="button" className="btn secondary small" onClick={() => void activate(r)}>{t('agents.action.activate')}</button>}
        </span>
      ),
    },
  ];

  return (
    <>
      <h2>{t('agents.templates.title')}</h2>
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}
      {notice && <div className="ok" style={{ marginBottom: 'var(--sp-3)' }}>{notice}</div>}
      <div className="agent-table">
        <Table columns={columns} rows={rows ?? []} rowKey={(r) => r.id} empty={rows === null ? t('common.loading') : t('agents.templates.empty')} defaultSort={{ key: 'slug', dir: 'asc' }} />
      </div>
      {open && (
        <Drawer title={t('agents.templates.drawer', { name: open.name })} onClose={() => setOpen(null)} badge={t('agents.templates.version', { n: open.currentVersion })}>
          <div className="stack agent-drawer">
            <div>{statusBadge(open.status)}</div>
            <strong className="muted">{t('agents.templates.purpose')}</strong>
            <div>{open.purpose}</div>
            <strong className="muted">{t('agents.templates.defaultTools')}</strong>
            <div className="row">{open.defaultTools.map((ref) => <span key={ref} className="badge">{ref}</span>)}</div>
            <strong className="muted">{t('agents.templates.instruction')}</strong>
            <div className="agent-instruction">{open.baseInstruction}</div>
            {open.status === 'draft' && <div className="note">{t('agents.templates.activateHint')}</div>}
            <div className="row">
              {canManage && open.status === 'draft' && <button type="button" className="btn" onClick={() => void activate(open)}>{t('agents.action.activate')}</button>}
              <button type="button" className="btn secondary" onClick={() => setOpen(null)}>{t('common.close')}</button>
            </div>
          </div>
        </Drawer>
      )}
    </>
  );
}

// -------------------------------------------------------------------- tools

function Tools({ canManage, canManagePolicy }: { canManage: boolean; canManagePolicy: boolean }) {
  const [rows, setRows] = useState<AgentTool[] | null>(null);
  const [officeOpen, setOfficeOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => agentsApi.tools().then(setRows).catch((e) => setError(errorText(e)));
  useEffect(() => { void load(); }, []);

  const toggle = async (row: AgentTool) => {
    setError(null);
    try {
      await agentsApi.setToolEnabled(row.id, !row.enabled);
      await load();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const columns: Column<AgentTool>[] = [
    { key: 'server', title: t('agents.tools.col.server'), render: (r) => r.mcpServer, sortValue: (r) => r.mcpServer },
    { key: 'tool', title: t('agents.tools.col.tool'), render: (r) => r.toolName, sortValue: (r) => r.toolName },
    { key: 'effect', title: t('agents.tools.col.effect'), render: (r) => <span className={r.sideEffect === 'irreversible' ? 'badge danger' : r.sideEffect === 'reversible' ? 'badge ok' : 'badge'}>{t(EFFECT_KEY[r.sideEffect])}</span>, sortValue: (r) => r.sideEffect },
    { key: 'idempotent', title: t('agents.tools.col.idempotent'), render: (r) => t(r.idempotent ? 'common.yes' : 'common.no'), sortValue: (r) => (r.idempotent ? 1 : 0) },
    { key: 'cost', title: t('agents.tools.col.cost'), render: (r) => r.costClass, sortValue: (r) => r.costClass },
    {
      key: 'enabled',
      title: t('agents.tools.col.enabled'),
      render: (r) => <input type="checkbox" className="switch" checked={r.enabled} disabled={!canManage} onChange={() => void toggle(r)} aria-label={`${t('agents.tools.col.enabled')}: ${r.ref}`} />,
      sortValue: (r) => (r.enabled ? 1 : 0),
    },
  ];

  return (
    <>
      <div className="row between">
        <h2 style={{ margin: 0 }}>{t('agents.tools.title')}</h2>
        {canManagePolicy && <button type="button" className="btn secondary" onClick={() => setOfficeOpen(true)}>{t('agents.tools.officePolicy')}</button>}
      </div>
      <p className="muted">{t('agents.tools.officeHint')}</p>
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}
      <div className="agent-table">
        <Table columns={columns} rows={rows ?? []} rowKey={(r) => r.id} empty={rows === null ? t('common.loading') : t('agents.tools.empty')} defaultSort={{ key: 'server', dir: 'asc' }} />
      </div>
      {officeOpen && <PolicyDrawer instance={null} canManage={canManagePolicy} onClose={() => setOfficeOpen(false)} onSaved={() => void load()} />}
    </>
  );
}

// ------------------------------------------------------------------- models

function Models({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<AgentModel[] | null>(null);
  const [form, setForm] = useState<AgentModel | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => agentsApi.models().then(setRows).catch((e) => setError(errorText(e)));
  useEffect(() => { void load(); }, []);

  const toggle = async (row: AgentModel) => {
    setError(null);
    try {
      await agentsApi.updateModel(row.id, { enabled: !row.enabled });
      await load();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const columns: Column<AgentModel>[] = [
    { key: 'provider', title: t('agents.models.col.provider'), render: (r) => r.provider, sortValue: (r) => r.provider },
    { key: 'model', title: t('agents.models.col.model'), render: (r) => r.model, sortValue: (r) => r.model },
    { key: 'in', title: t('agents.models.col.input'), render: (r) => money(r.inputPerMTok), sortValue: (r) => Number(r.inputPerMTok) },
    { key: 'out', title: t('agents.models.col.output'), render: (r) => money(r.outputPerMTok), sortValue: (r) => Number(r.outputPerMTok) },
    { key: 'cached', title: t('agents.models.col.cached'), render: (r) => money(r.cachedPerMTok), sortValue: (r) => Number(r.cachedPerMTok) },
    {
      key: 'enabled',
      title: t('agents.models.col.enabled'),
      render: (r) => <input type="checkbox" className="switch" checked={r.enabled} disabled={!canManage} onChange={() => void toggle(r)} aria-label={`${t('agents.models.col.enabled')}: ${r.model}`} />,
      sortValue: (r) => (r.enabled ? 1 : 0),
    },
    { key: 'actions', title: '', render: (r) => (canManage ? <button type="button" className="btn secondary small" onClick={() => setForm(r)}>{t('agents.action.edit')}</button> : null) },
  ];

  return (
    <>
      <div className="row between">
        <h2 style={{ margin: 0 }}>{t('agents.models.title')}</h2>
        {canManage && <button type="button" className="btn" onClick={() => setForm('new')}>{t('agents.models.new')}</button>}
      </div>
      <p className="muted">{t('agents.models.hint')}</p>
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}
      <div className="agent-table">
        <Table columns={columns} rows={rows ?? []} rowKey={(r) => r.id} empty={rows === null ? t('common.loading') : t('agents.models.empty')} defaultSort={{ key: 'provider', dir: 'asc' }} />
      </div>
      {form && <ModelDrawer model={form === 'new' ? null : form} onClose={() => setForm(null)} onSaved={() => { setForm(null); void load(); }} />}
    </>
  );
}

function ModelDrawer({ model, onClose, onSaved }: { model: AgentModel | null; onClose: () => void; onSaved: () => void }) {
  const [provider, setProvider] = useState(model?.provider ?? '');
  const [name, setName] = useState(model?.model ?? '');
  const [input, setInput] = useState(model?.inputPerMTok ?? '');
  const [output, setOutput] = useState(model?.outputPerMTok ?? '');
  const [cached, setCached] = useState(model?.cachedPerMTok ?? '0');
  const [currency, setCurrency] = useState(model?.currency ?? 'USD');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (model) await agentsApi.updateModel(model.id, { inputPerMTok: input.trim(), outputPerMTok: output.trim(), cachedPerMTok: cached.trim(), currency });
      else await agentsApi.createModel({ provider: provider.trim(), model: name.trim(), inputPerMTok: input.trim(), outputPerMTok: output.trim(), cachedPerMTok: cached.trim(), currency });
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const title = model ? t('agents.models.drawerEdit', { name: model.model }) : t('agents.models.drawerNew');
  return (
    <Drawer title={title} onClose={onClose}>
      <form className="form agent-drawer" onSubmit={submit} aria-label={title}>
        <div className="row">
          <label>
            {t('agents.models.col.provider')}
            <input value={provider} onChange={(e) => setProvider(e.target.value)} required readOnly={!!model} maxLength={60} />
          </label>
          <label>
            {t('agents.models.col.model')}
            <input value={name} onChange={(e) => setName(e.target.value)} required readOnly={!!model} maxLength={120} />
          </label>
        </div>
        <div className="row">
          <label>
            {t('agents.models.col.input')}
            <input value={input} onChange={(e) => setInput(e.target.value)} required inputMode="decimal" />
          </label>
          <label>
            {t('agents.models.col.output')}
            <input value={output} onChange={(e) => setOutput(e.target.value)} required inputMode="decimal" />
          </label>
        </div>
        <div className="row">
          <label>
            {t('agents.models.col.cached')}
            <input value={cached} onChange={(e) => setCached(e.target.value)} inputMode="decimal" />
          </label>
          <label>
            {t('agents.models.currency')}
            <input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} />
          </label>
        </div>
        {error && <div className="error">{error}</div>}
        <div className="row">
          <button className="btn" type="submit" disabled={busy}>{t('common.save')}</button>
          <button className="btn secondary" type="button" onClick={onClose}>{t('common.close')}</button>
        </div>
      </form>
    </Drawer>
  );
}

// ------------------------------------------------------------------ library

function Library() {
  const [history, setHistory] = useState<PackageHistoryRow[]>([]);
  const [result, setResult] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reload = () => api.packageHistory().then(setHistory).catch((e: Error) => setError(e.message));
  useEffect(() => {
    void reload();
  }, []);

  const exportPkg = async () => {
    setBusy(true);
    setError(null);
    try {
      const name = `library-${new Date().toISOString().slice(0, 10)}`;
      const pkg = await api.exportPackage(name);
      const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${name}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const importPkg = async (file: File) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const pkg = JSON.parse(await file.text()) as unknown;
      setResult(await api.importPackage(pkg));
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const columns: Column<PackageHistoryRow>[] = [
    { key: 'when', title: t('agents.library.col.when'), render: (r) => fmt(r.createdAt), sortValue: (r) => r.createdAt },
    { key: 'name', title: t('agents.library.col.name'), render: (r) => r.name, sortValue: (r) => r.name },
    { key: 'dir', title: t('agents.library.col.direction'), render: (r) => <span className="badge">{t(r.direction === 'export' ? 'agents.library.export.dir' : 'agents.library.import.dir')}</span>, sortValue: (r) => r.direction },
    { key: 'sum', title: t('agents.library.col.checksum'), render: (r) => <span className="mono">{r.checksum.slice(0, 12)}</span> },
  ];

  const sum = (s: { created: number; updated: number; skipped: number }) => `${s.created}/${s.updated}/${s.skipped}`;

  return (
    <>
      <h2>{t('agents.library')}</h2>
      <div className="card stack">
        <div className="muted">{t('agents.library.hint')}</div>
        <div className="row">
          <button className="btn secondary" onClick={exportPkg} disabled={busy}>{t('agents.library.export')}</button>
          <button className="btn secondary" onClick={() => fileRef.current?.click()} disabled={busy}>{t('agents.library.import')}</button>
          <input ref={fileRef} type="file" accept="application/json" hidden onChange={(e) => e.target.files?.[0] && importPkg(e.target.files[0])} />
        </div>
        {result && <div className="ok">{t('agents.library.importResult', { t: sum(result.roleTemplates), s: sum(result.skills), r: sum(result.recipes) })}</div>}
        {error && <div className="error">{error}</div>}
      </div>
      <h2>{t('agents.library.history')}</h2>
      <Table columns={columns} rows={history} rowKey={(r) => r.id} empty={t('common.none')} />
    </>
  );
}

// ------------------------------------------------------------------- drawer

/** Registry pattern "drawer": right side panel, full screen under 900px (styles/base.css). */
function Drawer({ title, badge, onClose, children }: { title: string; badge?: string | undefined; onClose: () => void; children: ReactNode }) {
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="row between">
          <strong>{title}</strong>
          <span className="row">
            {badge && <span className="badge">{badge}</span>}
            <button type="button" className="btn secondary small" onClick={onClose}>{t('common.close')}</button>
          </span>
        </div>
        {children}
      </div>
    </>
  );
}
